/**
 * Create or edit a tour, in a modal.
 *
 * There is no step authoring here, and that is the whole reason this is a modal rather than a
 * screen: a tour's steps live in the dashboard's own code, next to the page component, and the
 * BFF owns only which page has a tour, whether it is active, and which revision people are on.
 * That is three fields.
 *
 * On create, only pages without a tour are offered — a page holds at most one. On edit, BU and
 * page are locked: moving a tour to another page would silently reassign its reach.
 *
 * @module Pages/BUs/Tours
 */

import { css } from '@emotion/css';
import { UTButton, UTModal, UTSelect } from '@group-link-one/gl-ui/utilities';
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useBumpTourVersion } from '../../Hooks/useBumpTourVersion';
import { useCreateTour } from '../../Hooks/useCreateTour';
import { useTours } from '../../Hooks/useTours';
import { useUpdateTour } from '../../Hooks/useUpdateTour';
import type { Tour } from '../../Types/Tours.types';
import { useTenants } from '@/Hooks/useTenants';
import { usePages } from '@/Pages/BUs/Pages/Hooks/usePagesQueries';
import { getTourRegistryEntry, resolveBrand } from '@/Shared/brand';
import { Switch } from '@/Shared/components/Switch/Switch';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  width: 100%;
  min-width: 0;
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const lockedStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-m);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const lockedCellStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const lockedLabelStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const lockedValueStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const switchBoxStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
`;

const switchTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const switchTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const spacerStyle = css`
  flex: 1;
`;

const versionStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
`;

const warningStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
`;

const warningTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const registryCodeStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface TourFormModalProps {
  open: boolean;
  /** The tour being edited; absent means a new one. */
  tour?: Tour;
  onClose: () => void;
}

export const TourFormModal = ({ open, tour, onClose }: TourFormModalProps): JSX.Element => {
  const isEditing = tour !== undefined;

  const { tenants, tenantOptions } = useTenants();
  const { data: pagesData } = usePages();
  const { data: toursData } = useTours();

  const createTour = useCreateTour();
  const updateTour = useUpdateTour();
  const bumpVersion = useBumpTourVersion();

  const [tenantSlug, setTenantSlug] = useState(tour?.tenantSlug ?? '');
  const [pageId, setPageId] = useState(tour?.pageId ?? '');
  const [isActive, setIsActive] = useState(tour?.isActive ?? true);
  const [shouldBump, setShouldBump] = useState(false);

  // Reset between openings so a previous tour never bleeds into the next.
  useEffect(() => {
    if (!open) return;
    setTenantSlug(tour?.tenantSlug ?? '');
    setPageId(tour?.pageId ?? '');
    setIsActive(tour?.isActive ?? true);
    setShouldBump(false);
  }, [open, tour]);

  const allPages = useMemo(() => pagesData?.data ?? [], [pagesData]);
  const allTours = useMemo(() => toursData?.data ?? [], [toursData]);

  /** Pages of the chosen BU that have no tour yet — a page holds at most one. */
  const pageOptions = useMemo(() => {
    const pageIdsWithTour = new Set(
      allTours.filter(item => item.id !== tour?.id).map(item => item.pageId),
    );

    return allPages
      .filter(page => page.tenantSlug === tenantSlug && !pageIdsWithTour.has(page.id))
      .map(page => ({ id: page.id, text: `${page.name} (${page.slug})`, value: page.id }));
  }, [allPages, allTours, tenantSlug, tour?.id]);

  const selectedPage = allPages.find(page => page.id === pageId);

  const registryEntry = useMemo(() => {
    if (!selectedPage) return undefined;
    const tenant = tenants.find(item => item.slug === selectedPage.tenantSlug);
    return getTourRegistryEntry(
      resolveBrand({ slug: selectedPage.tenantSlug, brand: tenant?.brand }),
      selectedPage.slug,
    );
  }, [selectedPage, tenants]);

  const isValid = tenantSlug.length > 0 && pageId.length > 0;
  const isSaving = createTour.isPending || updateTour.isPending || bumpVersion.isPending;
  const error =
    createTour.error?.message ?? updateTour.error?.message ?? bumpVersion.error?.message ?? null;

  const handleSave = () => {
    if (isEditing) {
      updateTour.mutate(
        { id: tour.id, payload: { isActive } },
        {
          onSuccess: () => {
            if (shouldBump) {
              bumpVersion.mutate(tour.id, { onSuccess: onClose });
              return;
            }
            onClose();
          },
        },
      );
      return;
    }

    createTour.mutate({ pageId, isActive }, { onSuccess: onClose });
  };

  const content = (
    <div className={bodyStyle}>
      <p className={hintStyle}>
        {isEditing
          ? 'BU e página não mudam depois de criado: mover o tour reatribuiria o alcance dele.'
          : 'Os passos já estão no código do dashboard. Só páginas sem tour aparecem na lista.'}
      </p>

      {isEditing ? (
        /*
          Read-only text rather than two disabled selects. A disabled UTSelect shows its
          placeholder, not the value, so the person would be looking at "Selecione a BU" for a
          tour that plainly has one.
        */
        <div className={lockedStyle}>
          <div className={lockedCellStyle}>
            <span className={lockedLabelStyle}>Business unit</span>
            <span className={lockedValueStyle}>{tour.tenantSlug}</span>
          </div>
          <div className={lockedCellStyle}>
            <span className={lockedLabelStyle}>Página</span>
            <span className={lockedValueStyle}>{tour.pageSlug}</span>
          </div>
        </div>
      ) : (
        <>
          <UTSelect
            options={tenantOptions.map(option => ({
              id: String(option.id),
              text: option.text,
              value: String(option.value),
            }))}
            canSelectMultiple={false}
            hasCheckbox={false}
            inputOptions={{
              textFieldProps: {
                topLabel: 'Business unit',
                placeholder: 'Selecione a BU',
                required: true,
              },
            }}
            onSelectedOptionsChange={options => {
              setTenantSlug(options.length > 0 ? String(options[0].value) : '');
              setPageId('');
            }}
          />

          <UTSelect
            key={tenantSlug}
            options={pageOptions}
            canSelectMultiple={false}
            hasCheckbox={false}
            inputOptions={{
              textFieldProps: {
                topLabel: 'Página',
                placeholder: tenantSlug ? 'Selecione a página' : 'Escolha a business unit primeiro',
                disabled: !tenantSlug,
                required: true,
              },
            }}
            onSelectedOptionsChange={options => {
              setPageId(options.length > 0 ? String(options[0].value) : '');
            }}
          />
        </>
      )}

      {!isEditing && tenantSlug && pageOptions.length === 0 ? (
        <p className={hintStyle}>
          Todas as páginas desta BU já têm tour. Crie uma página nova ou edite o tour existente.
        </p>
      ) : null}

      <div className={switchBoxStyle}>
        <div className={switchTextStyle}>
          <span className={switchTitleStyle}>Tour ativo</span>
          <span className={hintStyle}>Inativo, o tour fica salvo mas não abre para ninguém.</span>
        </div>
        <div className={spacerStyle} />
        <Switch
          id='tour-form-active'
          size='md'
          checked={isActive}
          aria-label='Tour ativo'
          onChange={setIsActive}
        />
      </div>

      {isEditing ? (
        <div className={switchBoxStyle}>
          <div className={switchTextStyle}>
            <span className={switchTitleStyle}>
              Subir revisão ·{' '}
              <span className={versionStyle}>v{tour.version + (shouldBump ? 1 : 0)}</span>
            </span>
            <span className={hintStyle}>
              {shouldBump
                ? `Ao salvar, sobe para v${tour.version + 1} e o tour reabre para todo mundo. O alcance zera.`
                : 'Subir a revisão ao salvar reexibe o tour para quem já viu.'}
            </span>
          </div>
          <div className={spacerStyle} />
          <Switch
            id='tour-form-bump'
            size='md'
            checked={shouldBump}
            aria-label='Subir revisão ao salvar'
            onChange={setShouldBump}
          />
        </div>
      ) : null}

      {/*
        Only the part of the old right-hand column that says something: the page is about to get a
        tour the dashboard has no steps for. The rest of that column explained where steps live,
        which is what the hint at the top already says.
      */}
      {selectedPage && !registryEntry ? (
        <div className={warningStyle}>
          <span className={warningTitleStyle}>
            O dashboard desta BU não tem passos para {selectedPage.slug}
          </span>
          <span className={registryCodeStyle}>
            O tour é entregue, mas não abre nada até alguém adicionar a entrada em TOUR_PAGES.
          </span>
        </div>
      ) : null}

      {error !== null ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='secondary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='tour-form-save'
          text={isSaving ? 'Salvando...' : 'Salvar'}
          icon='Communication.Check'
          disabled={!isValid || isSaving}
          onClick={handleSave}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Editar tour' : 'Criar tour'}
      content={content}
      maxWidth='520px'
      hasButtonClose
      position='center'
    />
  );
};
